import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';

import GlobalWrapper from '../../../components/Wrapper/GlobalWrapper';
import SellingWingFilter from './SellingWingFilter';
import OgdPointTracker from './OgdPointTracker';
import { clearOgdPointTracker } from '../../../redux/features/poinTrackerSlice';
import apiRequest from '../../../api/request';

/**
 * OGD Point Tracker, the page.
 *
 * Route: /Point-Tracker/ogd-point-tracker
 *
 * A SECOND, INDEPENDENT copy of the table under Team Wise Analytics, Month Wise
 * Analysis. Its own route, its own endpoint and its own slice entry, sharing
 * nothing with the live table, so this one can be rebuilt column by column
 * without any risk to it.
 *
 * The month and team filter is this page's own, SellingWingFilter, because this
 * table opens on all five selling squads at once and the shared filter can only
 * hold one team at a time.
 *
 * See docs/point-tracker-rework/ in the node portal repo for the first attempt.
 * Reference only, the logic and the database have both changed since.
 */
/**
 * The month that is running right now, first day to last day.
 *
 * The page always opens on this. February, a 31 day month and a leap year are all
 * handled by asking for day 0 of the NEXT month, which JavaScript reads as the
 * last day of this one, rather than by keeping a table of month lengths.
 */
const thisMonth = () => {
  const pad = (n) => String(n).padStart(2, '0');
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
  return {
    key: stamp,
    from: `${stamp}-01`,
    to: `${stamp}-${pad(end.getDate())}`,
  };
};

/**
 * Does this person have a target for the month.
 *
 * Read off the two target columns the table already loaded, so no extra call is
 * made. Either one counts: a head can set a revenue target and no slot, or the
 * other way round, and both mean somebody committed a number for them.
 *
 * Exported because the table filters with the SAME test the switch counts with.
 * Two copies of this would drift and the switch would promise a number it did
 * not deliver.
 */
export const hasTarget = (row) =>
  Number(row?.revenueTarget) > 0 || Number(row?.slotTarget) > 0;

const OgdPointTrackerLanding = () => {
  // the month the page opens on, worked out fresh on every load
  const CURRENT_MONTH = thisMonth();

  // the filter choices survive a reload, same as the other point tracker pages.
  // THE KEY CARRIES THE MONTH, on purpose. Whatever dates somebody picks are kept
  // while that month is running, and the moment a new month starts the saved key
  // no longer matches, so the page opens on the new month rather than on last
  // month's leftovers.
  const storedData = localStorage.getItem(
    `OgdPointTrackerLanding-${CURRENT_MONTH.key}`,
  );
  const parsedStoredData = storedData ? JSON.parse(storedData) : {};

  /**
   * The squads the table is showing. A LIST, not one team, because the sheet this
   * table has to match covers all five selling squads together.
   *
   * EMPTY MEANS "THE SELLING SQUADS", and the server decides which those are.
   *
   * The list of selling squads is written down in ONE place, SELLING_WINGS in the
   * backend, and travels here on every response as meta.defaultWingIds. Keeping a
   * second copy in this file was the obvious thing to do and the wrong thing: two
   * lists drift apart, and the day they disagree the screen ticks one set of
   * squads while the table shows another.
   *
   * So the page starts empty, asks for nothing in particular, and ticks whatever
   * the server says the selling squads are.
   */
  const [selectedWings, setSelectedWings] = useState(
    parsedStoredData?.selectedWings || [],
  );

  // what the server calls the selling squads. Used for the ticks on first load,
  // for the Reset button, and to tell a selling squad from a borrowed one.
  const [defaultWings, setDefaultWings] = useState([]);

  /**
   * Hide everybody with no target for the month.
   *
   * The table lists the whole squad from the 1st, which is the point of it, but
   * that makes it longer than the sheet the department keeps. Somebody with a
   * target is somebody their head has committed a number for, so this is the
   * quickest way to line the two up.
   *
   * OFF by default. The full list is the honest one, and a row of zeros is worth
   * seeing rather than hiding by default.
   */
  const [onlyWithTarget, setOnlyWithTarget] = useState(
    Boolean(parsedStoredData?.onlyWithTarget),
  );
  const [fromDate, setFromDate] = useState(
    parsedStoredData?.fromDate || CURRENT_MONTH.from,
  );
  const [toDate, setToDate] = useState(parsedStoredData?.toDate || CURRENT_MONTH.to);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // the table below fetches the rows, and the answer carries the squad list with
  // it, so nothing extra has to be requested just to draw the tick boxes
  const meta = useSelector(
    (state) => state.pointTracker.ogdPointTracker?.responseData?.data?.meta,
  );

  // read here as well as in the table below, only to count how many people have
  // a target so the switch can say so before anybody flips it
  const loadedRows = useSelector(
    (state) => state.pointTracker.ogdPointTracker?.responseData?.data?.rows,
  );
  const withTargetCount = (loadedRows || []).filter(hasTarget).length;

  /**
   * Take the selling squads from the answer.
   *
   * Runs on every response, which is harmless: both setters compare first and
   * keep what they have if nothing moved, so this cannot loop.
   *
   * The ticks are only filled in when nothing is ticked yet, so it never
   * overrides a squad somebody ticked on by hand.
   */
  useEffect(() => {
    const fallback = meta?.defaultWingIds;
    if (!fallback?.length) return;
    setDefaultWings((current) =>
      current.join() === fallback.join() ? current : fallback,
    );
    setSelectedWings((current) => (current.length ? current : fallback));
  }, [meta?.defaultWingIds]);

  /**
   * The Targets button is ALWAYS SHOWN, and only enabled for somebody with people
   * reporting to them.
   *
   * Shown to everybody on purpose. A button that appears for one person and not
   * another leaves the second wondering whether the feature exists at all, or
   * whether something is broken. Disabled with a reason on hover answers that
   * without them having to ask.
   *
   * A head is somebody IN OGD who also has working OGD people reporting to them.
   * Both tests have to pass. The org chart decides it, there is no permission
   * flag. Disabling the button is a courtesy, never the security: the page itself
   * refuses anybody who fails either test, and the server re-checks on every
   * single save.
   */
  const [isHead, setIsHead] = useState(false);
  // false when they are outside OGD, which is a different reason from having no
  // team, and the hover text says which
  const [inOgd, setInOgd] = useState(true);

  /**
   * THE WINDOW. Targets can only be set between the 1st and the 5th.
   *
   * Read from the SERVER clock, never from `new Date()` here, so a laptop with
   * the wrong date cannot make a closed window look open. The server refuses the
   * save either way; this is only so the screen tells the truth.
   */
  const [gate, setGate] = useState(null);
  const [windowNotice, setWindowNotice] = useState(false);
  // Finance correct targets after the fact, so no window applies to them
  const [isFinance, setIsFinance] = useState(false);
  // until the answer arrives nobody is a head OR not a head, and the button says
  // so rather than flashing enabled and then dying
  const [checkingHead, setCheckingHead] = useState(true);

  useEffect(() => {
    (async () => {
      const res = await apiRequest({ method: 'get', url: 'ogd-point-tracker/my-team' });
      const role = res?.data?.meta?.role;
      // Finance are not a head and never will be, but the screen is theirs too
      setIsHead(Boolean(res?.data?.isHead) || role === 'FINANCE');
      setIsFinance(role === 'FINANCE');
      setInOgd(res?.data?.meta?.inOgd !== false);
      setGate(res?.data?.meta?.window || null);
      setCheckingHead(false);
    })();
  }, []);

  // no answer yet means do not claim the window is shut, and Finance are never shut
  const windowOpen = isFinance || (gate ? Boolean(gate.open) : true);

  /** Four different reasons, and the hover says which one applies. */
  const targetsButtonReason = () => {
    if (checkingHead) return 'Checking whether anybody reports to you';

    if (!isHead) {
      return inOgd
        ? 'Only for somebody with people reporting to them. Targets are set by the ' +
            'person a squad reports to. If you should be able to set them, ask HR ' +
            'to check your team on the Employee Tagging screen.'
        : 'Targets are set by a reporting head inside the OGD department. Your own ' +
            'department is somewhere else, so you cannot set them even if OGD ' +
            'people report to you.';
    }

    if (isFinance) {
      return 'Correct the revenue and slot targets for any OGD person, any month.';
    }

    const lastDay = gate?.lastDay || 5;
    return windowOpen
      ? 'Set the monthly revenue and slot targets for your team. Open until the ' +
          lastDay +
          ' of the month, and fixed once saved.'
      : 'Targets can only be set between the 1st and the ' +
          lastDay +
          ' of the month. Today is the ' +
          gate?.day +
          '. Click to see when it opens again.';
  };

  useEffect(() => {
    localStorage.setItem(
      `OgdPointTrackerLanding-${CURRENT_MONTH.key}`,
      JSON.stringify({ selectedWings, fromDate, toDate, onlyWithTarget }),
    );
  }, [selectedWings, fromDate, toDate, onlyWithTarget]);

  // leave nothing behind for the next page that opens this one
  useEffect(() => {
    return () => {
      dispatch(clearOgdPointTracker());
    };
  }, [dispatch]);

  /**
   * Forget the saved filter and go back to the default: this month, the five
   * selling squads.
   *
   * The filter survives a reload on purpose, which is handy until it is not:
   * somebody looks at March, comes back a week later and is still looking at
   * March without noticing. This is the way out of that, without having to know
   * what localStorage is.
   *
   * It clears EVERY saved key, not only this month's, so an old month left
   * behind by a previous visit goes too.
   */
  const resetFilter = () => {
    Object.keys(localStorage)
      .filter((k) => k.startsWith('OgdPointTrackerLanding'))
      .forEach((k) => localStorage.removeItem(k));
    setSelectedWings(defaultWings);
    setFromDate(CURRENT_MONTH.from);
    setToDate(CURRENT_MONTH.to);
    setOnlyWithTarget(false);
  };

  // whether anything is actually off the default, so the button can say so.
  // Sorted before comparing, so ticking a squad off and back on again does not
  // leave the button looking as though something is still changed. Before the
  // first answer arrives there is nothing to compare against, so nothing counts
  // as changed.
  const sameWings =
    defaultWings.length === 0 ||
    (selectedWings.length === defaultWings.length &&
      [...selectedWings].sort().join() === [...defaultWings].sort().join());

  const filterChanged =
    fromDate !== CURRENT_MONTH.from ||
    toDate !== CURRENT_MONTH.to ||
    !sameWings ||
    onlyWithTarget;

  return (
    <GlobalWrapper
      title={'OGD Point Tracker'}
      actions={
        <Stack direction="row" spacing={1} alignItems="center">
        <Tooltip
          title={
            filterChanged
              ? 'Back to this month and the five selling squads'
              : 'Already showing this month and the five selling squads'
          }
        >
          <span>
            <Button
              variant="outlined"
              size="small"
              color="inherit"
              disabled={!filterChanged}
              onClick={resetFilter}
              startIcon={<Icon icon="mdi:filter-remove-outline" />}
            >
              Reset filter
            </Button>
          </span>
        </Tooltip>

        <Tooltip title={targetsButtonReason()}>
          {/* a disabled button fires no events, so the tooltip needs a live
              element of its own to hang off */}
          <span>
            <Button
              variant="outlined"
              size="small"
              disabled={checkingHead || !isHead}
              // outside the window the button stays CLICKABLE and explains itself.
              // A dead button tells nobody when it comes back.
              onClick={() =>
                windowOpen
                  ? navigate('/Point-Tracker/target-slot-fill')
                  : setWindowNotice(true)
              }
              startIcon={
                <Icon icon={windowOpen ? 'mdi:target-account' : 'mdi:calendar-lock'} />
              }
              sx={windowOpen ? {} : { opacity: 0.55 }}
            >
              Targets and Slots
            </Button>
          </span>
        </Tooltip>
        </Stack>
      }
    >
      <Box>
        <SellingWingFilter
          fromDate={fromDate}
          toDate={toDate}
          setFromDate={setFromDate}
          setToDate={setToDate}
          selectedWings={selectedWings}
          setSelectedWings={setSelectedWings}
          defaultWings={defaultWings}
          onlyWithTarget={onlyWithTarget}
          setOnlyWithTarget={setOnlyWithTarget}
          withTargetCount={withTargetCount}
          totalCount={(loadedRows || []).length}
        />
        <OgdPointTracker
          fromDate={fromDate}
          toDate={toDate}
          selectedWings={selectedWings}
          onlyWithTarget={onlyWithTarget}
        />
      </Box>

      <Dialog
        open={windowNotice}
        onClose={() => setWindowNotice(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
            <Typography variant="subtitle1" fontWeight={600}>
              Setting targets is closed
            </Typography>
            <IconButton size="small" onClick={() => setWindowNotice(false)}>
              <Icon icon="mdi:close" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2">
              Targets can only be set between the{' '}
              <strong>1st and the {gate?.lastDay || 5}</strong> of the month. Today is
              the <strong>{gate?.day}</strong>.
            </Typography>
            <Alert severity="info" icon={<Icon icon="mdi:calendar-clock" />}>
              It opens again on <strong>{gate?.opensAgain}</strong>.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              A target is a promise made before the month is played, not a line drawn
              once the result is known. Once saved it is fixed and cannot be changed.
            </Typography>
          </Stack>
        </DialogContent>
      </Dialog>
    </GlobalWrapper>
  );
};

export default OgdPointTrackerLanding;
